import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const Rental = () => {
  const today = new Date().toISOString().split('T')[0];
  const { userData, selectedCenter } = useUser();
  const userId = userData?.userId;
  const token = localStorage.getItem('auth-token');
  const [academies, setAcademies] = useState([]);
  const [academyOptions, setAcademyOptions] = useState([]);
  const [sportsOptions, setSportsOptions] = useState([]);
  const [rentalData, setRentalData] = useState(null);
  const [showPanel, setShowPanel] = useState(false);

  const [formData, setFormData] = useState({
    date: today,
    academy: null,
  });

  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);

  const buildSubmitPayload = useCallback(() => {
    const date = formData.date || today;
    const academyId = formData.academy?.value || null;

    const entries = sportsOptions.map((sport) => {
      const id = sport.value;
      const cash = Number(formData[`cash_${id}`] ?? 0) || 0;
      const venueQr = Number(formData[`venueQr_${id}`] ?? 0) || 0;
      const razorpay = Number(formData[`razorpay_${id}`] ?? 0) || 0;
      const comments = formData[`comments_${id}`] || '';
      const amountReceived = cash + venueQr + razorpay;

      return {
        sportId: id,
        sportName: sport.label,
        cash,
        venueQr,
        razorpay,
        amountReceived,
        comments,
      };
    }).filter(e => e.amountReceived > 0 || (e.comments && e.comments.trim().length));

    return {
      date,
      academyId,
      userId,
      entries,
    };
  }, [formData, sportsOptions, today, userId]);

  const buildExistingPayload = useCallback(() => {
    const date = formData.date || today;
    const academyId = formData.academy?.value || null;
    const existing = (rentalData?.data?.rentalData || []).map(e => ({
      sportId: e.sportId ?? null,                
      sportName: e.sportName || 'Unknown Sport',
      cash: e.cash || 0,
      venueQr: e.venueQr || 0,
      razorpay: e.razorpay || 0,
      amountReceived: e.amountReceived || 0,
      comments: e.comments || '',
    }));
    return { date, academyId, userId, entries: existing };
  }, [formData.date, formData.academy, rentalData, today, userId]);

  const redactForAudit = useCallback((payload) => {
    if (!payload) return payload;
    const copy = JSON.parse(JSON.stringify(payload));
    if (Array.isArray(copy.entries)) {
      copy.entries.forEach(e => {
        if (e?.comments) e.comments = '********';
      });
    }
    return copy;
  }, []);

  useEffect(() => {
    if (!formData.academy) return;
    const existingPayload = buildExistingPayload();
    const hasExisting = (existingPayload.entries || []).length > 0;

    if (!initializedBaselineRef.current) {
      baselineRef.current = hasExisting ? existingPayload : buildSubmitPayload();
      initializedBaselineRef.current = true;
    } else {
      baselineRef.current = hasExisting ? existingPayload : baselineRef.current;
    }
  }, [formData.academy, formData.date, rentalData, buildExistingPayload, buildSubmitPayload]);

  useEffect(() => {
    const fetchAcademies = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL_ACADEMIES, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const fetched = response.data.data.academies || [];
        const filtered =
          userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)
            ? fetched.filter(a => userData.academyIds.includes(a.id))
            : fetched;

        setAcademies(filtered);
        setAcademyOptions(
          filtered.map(a => ({ label: a.name, value: a.id }))
        );
      } catch (err) {
        console.error('Error fetching academies:', err);
      }
    };

    fetchAcademies();
  }, [userData?.userType, userData?.academyIds]);

  useEffect(() => {
    if (!selectedCenter || academies.length === 0) return;
    const found = academies.find(a => a.id === Number(selectedCenter));
    if (!found) return;

    const selectedOption = { label: found.name, value: found.id };
    setFormData(prev => ({ ...prev, academy: selectedOption }));

    if (found.sports) {
      setSportsOptions(
        found.sports.map(s => ({
          label: s.name,
          value: s.sport_id,
          image: s.sport_pic
        }))
      );
    } else {
      setSportsOptions([]);
    }
  }, [selectedCenter, academies]);

  const handleAcademyChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, academy: selectedOption }));
    const selected = academies.find(a => a.id === selectedOption?.value);
    setSportsOptions(
      selected?.sports
        ? selected.sports.map(s => ({ label: s.name, value: s.sport_id, image: s.sport_pic }))
        : []
    );

    setFormData(prev => {
      const next = { ...prev };
      sportsOptions.forEach(s => {
        delete next[`cash_${s.value}`];
        delete next[`venueQr_${s.value}`];
        delete next[`razorpay_${s.value}`];
        delete next[`comments_${s.value}`];
      });
      return next;
    });

    baselineRef.current = null;
    initializedBaselineRef.current = false;
  };

  const onlyDigits = (val) => (val === '' ? '' : /^\d+$/.test(val) ? parseInt(val, 10) : null);

  const fetchRentalData = useCallback(async () => {
    if (!formData.academy || !formData.date) return;
    try {
      const url = `${process.env.REACT_APP_API_URL_RENTAL_FETCH}?date=${formData.date}&academyId=${formData.academy.value}`;
      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
      setRentalData(response.data || null);
    } catch (err) {
      console.error('Error fetching rental data:', err);
      setRentalData(null);
    }
  }, [formData.academy, formData.date, token]);

  useEffect(() => {
    fetchRentalData();
  }, [fetchRentalData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { academy, date } = formData;

    if (!academy) {
      Swal.fire({
        title: 'Academy Not Selected!',
        text: 'Please select an academy.',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const payloads = [];
    const todayDate = new Date().toISOString().split('T')[0];

    sportsOptions.forEach((sport) => {
      const id = sport.value;
      const cash = Number(formData[`cash_${id}`] ?? 0) || 0;
      const venueQr = Number(formData[`venueQr_${id}`] ?? 0) || 0;
      const razorpay = Number(formData[`razorpay_${id}`] ?? 0) || 0;
      const comments = formData[`comments_${id}`] || '';
      const total = cash + venueQr + razorpay;

      if (total > 0 || (comments && comments.trim().length)) {
        payloads.push({
          id: null,
          date: date || todayDate,
          academyId: academy.value,
          sportId: id,
          amountReceived: total,
          venueQr,
          cash,
          razorpay,
          comments,
          userId: userId
        });
      }
    });

    if (payloads.length === 0) {
      Swal.fire({
        title: 'No Payments Entered',
        text: 'Please enter at least one payment entry for any sport.',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }
  
    try {
      const createdIds = [];
      for (const p of payloads) {
        const resp = await axios.post(process.env.REACT_APP_API_URL_RENTAL_POST, p, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
        const rid =
          resp?.data?.data?.id ??
          resp?.data?.id ??
          null;
        if (rid) createdIds.push(rid);
      }

      Swal.fire({
        title: 'Success!',
        text: 'Rental entries added successfully.',
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
  
      fireAudit({
        entity: 'rental',
        affected_id: `${academy.value}-${formData.date || todayDate}`,
        action: 'create',
        businessApi: process.env.REACT_APP_API_URL_RENTAL_POST,
        businessMethod: 'POST',
        oldPayload:payloads,
        newPayload: payloads,
        section: 'Centre Management',
        subSection: 'Walkin Sales(Rental)',
        extra: { page: 'Rental', createdIds, academyId: academy.value, date: formData.date || todayDate },
      });

      setFormData(prev => {
        const next = { ...prev };
        sportsOptions.forEach(s => {
          next[`cash_${s.value}`] = '';
          next[`venueQr_${s.value}`] = '';
          next[`razorpay_${s.value}`] = '';
          next[`comments_${s.value}`] = '';
        });
        return next;
      });

      fetchRentalData();
    } catch (error) {
      console.error('API Error:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to submit rental entries.',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const entries = rentalData?.data?.rentalData || [];
  const totalVenueQr = entries.reduce((sum, e) => sum + (e.venueQr || 0), 0);
  const totalCash = entries.reduce((sum, e) => sum + (e.cash || 0), 0);
  const totalRazorpay = entries.reduce((sum, e) => sum + (e.razorpay || 0), 0);
  const totalAmount = entries.reduce((sum, e) => sum + (e.amountReceived || 0), 0);

  return (
    <div style={{ margin: 'auto' }}>
      <form onSubmit={handleSubmit}>
        {/* Academy */}
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="academy"
            options={academyOptions}
            value={formData.academy}
            onChange={handleAcademyChange}
            placeholder="Select Academy"
            className={`walkin-form-dropdown ${formData.academy ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            menuPortalTarget={document.body}
            isClearable
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {formData.academy && <label htmlFor="academy">Academy</label>}
        </div>

        {entries.length > 0 && (
          <div style={{ marginBottom: '10px' }}>
            <div style={{ marginBottom: '10px', color: 'red' }}>
              Rental entry already exists for selected date. Please review before making another entry.
            </div>
            <button
              type="button"
              className="view-data-button"
              onClick={async () => {
                await fetchRentalData();
                setShowPanel(true);
              }}
            >
              View Rental Entries
            </button>
          </div>
        )}

        {/* Entry table */}
        {sportsOptions.length > 0 && (
          <div className="rental-entry-table-container">
            <label className="rental-entry-heading">Enter Rental Amounts</label>
            <div className="rental-table-scroll">
              <table className="rental-entry-table">
                <thead>
                  <tr>
                    <th>Sport</th>
                    <th>Cash (₹)</th>
                    <th>Venue QR (₹)</th>
                    <th>Razorpay (₹)</th>
                    <th>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {sportsOptions.map((sport) => (
                    <tr key={sport.value}>
                      <td>{sport.label}</td>
                      <td>
                        <input
                          type="text"
                          className="rental-input"
                          placeholder="0"
                          value={
                            formData[`cash_${sport.value}`] === 0
                              ? '0'
                              : (formData[`cash_${sport.value}`] ?? '')
                          }
                          onChange={(e) => {
                            const parsed = onlyDigits(e.target.value);
                            if (parsed !== null || e.target.value === '') {
                              setFormData(prev => ({ ...prev, [`cash_${sport.value}`]: e.target.value === '' ? '' : parsed }));
                            }
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="rental-input"
                          placeholder="0"
                          value={
                            formData[`venueQr_${sport.value}`] === 0
                              ? '0'
                              : (formData[`venueQr_${sport.value}`] ?? '')
                          }
                          onChange={(e) => {
                            const parsed = onlyDigits(e.target.value);
                            if (parsed !== null || e.target.value === '') {
                              setFormData(prev => ({ ...prev, [`venueQr_${sport.value}`]: e.target.value === '' ? '' : parsed }));
                            }
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="rental-input"
                          placeholder="0"
                          value={
                            formData[`razorpay_${sport.value}`] === 0
                              ? '0'
                              : (formData[`razorpay_${sport.value}`] ?? '')
                          }
                          onChange={(e) => {
                            const parsed = onlyDigits(e.target.value);
                            if (parsed !== null || e.target.value === '') {
                              setFormData(prev => ({ ...prev, [`razorpay_${sport.value}`]: e.target.value === '' ? '' : parsed }));
                            }
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="rental-input rental-comment"
                          placeholder="Optional comment"
                          value={formData[`comments_${sport.value}`] || ''}
                          onChange={(e) =>
                            setFormData(prev => ({ ...prev, [`comments_${sport.value}`]: e.target.value }))
                          }
                          maxLength={300}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          )}
            <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
              <button className="submit-button" type="submit">Submit</button>
            </div>
   
   
      </form>

      {showPanel && rentalData && (
        <div
          className="overlay"
          onClick={() => setShowPanel(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <div
            className="side-panel-rental"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              width: '400px',
              height: '100%',
              padding: '20px',
              overflowY: 'auto',
              boxShadow: '-2px 0px 5px rgba(0,0,0,0.5)',
            }}
          >
            <button onClick={() => setShowPanel(false)} style={{ float: 'right' }}>Close</button>
            <h3>Rental Entries</h3>
            <hr />
            <div style={{ marginBottom: 10, fontWeight: 600 }}>Total ₹{totalAmount.toLocaleString('en-IN')}
            </div>

            {(entries.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))).map((entry, idx) => (
              <div className="rental-entry" key={idx} style={{ borderBottom: '1px solid #eee', paddingBottom: 10, marginBottom: 10 }}>
                <p><strong>Time:</strong> {new Date(entry.createdAt).toLocaleString()}</p>
                <p><strong>Sport:</strong> {entry.sportName || 'Unknown Sport'}</p>
                <p><strong>Venue QR:</strong> ₹{(entry.venueQr || 0).toLocaleString('en-IN')}</p>
                <p><strong>Cash:</strong> ₹{(entry.cash || 0).toLocaleString('en-IN')}</p>
                <p><strong>Razorpay:</strong> ₹{(entry.razorpay || 0).toLocaleString('en-IN')}</p>
                <p><strong>Total:</strong> ₹{(entry.amountReceived || 0).toLocaleString('en-IN')}</p>
                {entry.comments && <p><strong>Comments:</strong> {entry.comments}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Rental;
